import { Anchor, Button, Divider, Stack, Text, TextInput } from "@mantine/core";
import * as React from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { track } from "../analytics/analytics";
import { fetchEnabledProviders, PROVIDER_LABEL, type OAuthProvider } from "../auth/providers";
import { CodeRateLimitedError, NoAccountError, requestEmailCode, signInWithProvider, verifyEmailCode } from "../auth/signIn";
import { GoogleLogo, MicrosoftLogo } from "../ui/brandLogos";
import { Notice } from "../ui/Notice";
import { PANEL_SPACE } from "../ui/tokens";
import { AuthCard } from "./AuthCard";

const PROVIDER_LOGO: Record<OAuthProvider, React.ReactNode> = { azure: <MicrosoftLogo />, google: <GoogleLogo /> };

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Sign-in and sign-up (firm-model spec §5.1, R24): email code, Microsoft, Google. Sign-up
 * creates the account; sign-in never does and points an unknown email at sign-up. Only
 * the providers Supabase reports as enabled are offered.
 */
export function SignInPage({
  mode,
  loadProviders = fetchEnabledProviders,
}: {
  mode: "sign-in" | "sign-up";
  loadProviders?: () => Promise<OAuthProvider[]>;
}) {
  const signUp = mode === "sign-up";
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [providers, setProviders] = React.useState<OAuthProvider[]>([]);
  const [step, setStep] = React.useState<"email" | "code">("email");
  const [email, setEmail] = React.useState(params.get("email") ?? "");
  const [code, setCode] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [noAccount, setNoAccount] = React.useState(false);

  React.useEffect(() => {
    let alive = true;
    void loadProviders().then((list) => alive && setProviders(list));
    return () => {
      alive = false;
    };
  }, [loadProviders]);

  const normalized = email.trim().toLowerCase();
  const after = params.get("next") || "/";

  const sendCode = async () => {
    setError(null);
    setNoAccount(false);
    if (!EMAIL.test(normalized)) {
      setError("Enter a valid email address.");
      return;
    }
    setBusy(true);
    try {
      await requestEmailCode(normalized, signUp);
      setStep("code");
      track(signUp ? "web_sign_up_code_sent" : "web_sign_in_code_sent");
    } catch (e) {
      if (e instanceof NoAccountError) setNoAccount(true);
      // D10: an invite or code sent moments ago puts the address in Supabase's cooldown.
      else if (e instanceof CodeRateLimitedError) setError("We just emailed this address. Wait a minute, then ask for a new code.");
      else setError("We couldn't send the code. Check the address and try again.");
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setError(null);
    if (!code.trim()) {
      setError("Enter the code from the email.");
      return;
    }
    setBusy(true);
    try {
      await verifyEmailCode(normalized, code.trim());
      track(signUp ? "web_sign_up_completed" : "web_sign_in_completed", { method: "email" });
      navigate(after, { replace: true });
    } catch {
      setError("That code didn't work. It may have expired, so send a new one.");
    } finally {
      setBusy(false);
    }
  };

  const withProvider = async (provider: OAuthProvider) => {
    setError(null);
    track(signUp ? "web_sign_up_oauth_started" : "web_sign_in_oauth_started", { provider });
    try {
      await signInWithProvider(provider, `${window.location.origin}${after}`);
    } catch {
      setError(`We couldn't start ${PROVIDER_LABEL[provider]} sign-in. Try again or use your email.`);
    }
  };

  return (
    <AuthCard title={signUp ? "Create your SlideBrain account" : "Sign in to SlideBrain"}>
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {noAccount ? (
        <Notice tone="info">
          No account for this email yet.{" "}
          <Anchor component={Link} to={`/sign-up?email=${encodeURIComponent(normalized)}`}>
            Create your account
          </Anchor>
        </Notice>
      ) : null}

      {step === "email" ? (
        <>
          {providers.length > 0 ? (
            <Stack gap={PANEL_SPACE.field}>
              {providers.map((p) => (
                <Button key={p} variant="default" leftSection={PROVIDER_LOGO[p]} onClick={() => void withProvider(p)}>
                  Continue with {PROVIDER_LABEL[p]}
                </Button>
              ))}
            </Stack>
          ) : null}
          {providers.length > 0 ? <Divider label={<Text c="dimmed" fz="sm">or use your email</Text>} labelPosition="center" /> : null}
          <Stack gap={PANEL_SPACE.field}>
            <TextInput
              label="Email Address"
              type="email"
              autoComplete="email"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              onKeyDown={(e) => e.key === "Enter" && void sendCode()}
            />
            <Button loading={busy} onClick={() => void sendCode()}>
              Email me a code
            </Button>
          </Stack>
        </>
      ) : (
        <Stack gap={PANEL_SPACE.field}>
          <Text>We emailed a 6-digit code to {normalized}. Enter it below.</Text>
          <TextInput
            label="One-Time Code"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="123456"
            value={code}
            onChange={(e) => setCode(e.currentTarget.value)}
            onKeyDown={(e) => e.key === "Enter" && void verify()}
          />
          <Button loading={busy} onClick={() => void verify()}>
            {signUp ? "Create account" : "Sign in"}
          </Button>
          <Anchor component="button" type="button" size="sm" w="fit-content" onClick={() => setStep("email")}>
            Use a different email or resend the code
          </Anchor>
        </Stack>
      )}

      <Stack gap={8}>
        {signUp ? (
          <Text lh={1.25}>
            Already have an account? <Anchor component={Link} to="/sign-in" fw={400}>Sign in</Anchor>
          </Text>
        ) : (
          <Text lh={1.25}>
            New to SlideBrain? <Anchor component={Link} to="/sign-up" fw={400}>Create an account</Anchor>
          </Text>
        )}
        {providers.indexOf("azure") !== -1 ? (
          <Text lh={1.25}>
            Work account blocked? <Anchor component={Link} to="/help/admin-consent" fw={400}>Ask your IT admin to approve SlideBrain</Anchor>
          </Text>
        ) : null}
      </Stack>
    </AuthCard>
  );
}
